import { V6Client } from "@aws-amplify/api-graphql";
import { MedicalAssistant, ModelMedicalAssistantFilterInput } from "~/api/API";
import { updateMedicalAssistantReturningID } from "~/api/graphql-custom/mutations";
import { listMedicalAssistantsOptimized } from "~/api/graphql-custom/queries";
import { errorReporter, toError } from "~/error-handling";
import { amplifyGraphQLClient } from "~/lib/amplify-client";

/**
 * Fetches all medical assistants with optional demo filtering.
 * @param client - The GraphQL client
 * @param isDemo - If true, only fetch demo MAs. If false, exclude demo MAs. If undefined, fetch all.
 */
export const fetchAllMedicalAssistants = async (
  client: V6Client,
  isDemo?: boolean,
): Promise<MedicalAssistant[]> => {
  // Build filter based on isDemo status
  const filter: ModelMedicalAssistantFilterInput | undefined =
    isDemo !== undefined
      ? isDemo
        ? { isDemo: { eq: true } } // Demo user: only show demo MAs
        : { isDemo: { ne: true } } // Non-demo user: exclude demo MAs
      : undefined;

  const fetchPage = async (
    nextToken?: string,
    accumulatedItems: MedicalAssistant[] = [],
  ): Promise<MedicalAssistant[]> => {
    const response = await client.graphql({
      query: listMedicalAssistantsOptimized,
      variables: { filter, nextToken },
    });

    if (response.errors) {
      throw new Error(
        `Failed to fetch medical assistants: ${response.errors.toString()}`,
      );
    }

    const newItems =
      response.data?.listMedicalAssistants.items || ([] as MedicalAssistant[]);
    const combinedItems = [
      ...accumulatedItems,
      ...newItems,
    ] as MedicalAssistant[];

    if (response.data?.listMedicalAssistants.nextToken) {
      return await fetchPage(
        response.data.listMedicalAssistants.nextToken,
        combinedItems,
      );
    } else {
      return combinedItems;
    }
  };

  const allMedicalAssistants = await fetchPage();

  // Sort the medical assistants by their 'name' field
  return allMedicalAssistants.sort(
    (a, b) => a.name?.localeCompare(b.name || "") || 0,
  );
};

export class MedicalAssistantService {
  private static instance: MedicalAssistantService;
  private readonly client: V6Client;

  private constructor(client: V6Client) {
    this.client = client;
  }

  static getInstance(): MedicalAssistantService {
    if (!MedicalAssistantService.instance) {
      MedicalAssistantService.instance = new MedicalAssistantService(
        amplifyGraphQLClient,
      );
    }
    return MedicalAssistantService.instance;
  }

  async updateDismissedProTips(
    assistantId: string,
    dismissedProTips: string[],
  ) {
    try {
      const response = await this.client.graphql({
        query: updateMedicalAssistantReturningID,
        variables: {
          input: {
            id: assistantId,
            dismissedProTips,
          },
        },
      });

      if (response.errors) {
        throw new Error(
          `Failed to update dismissed pro tips: ${response.errors.toString()}`,
        );
      }
    } catch (error) {
      const errObj = toError(error, "Failed to update dismissed pro tips");
      errorReporter.reportApplicationError(errObj, {
        tags: {
          component: "MedicalAssistantService",
          operation: "update_dismissed_pro_tips",
        },
        extra: {
          assistantId,
          dismissedProTips,
        },
      });
      throw errObj;
    }
  }
}

export const medicalAssistantService = MedicalAssistantService.getInstance();
